import { createRouter, createWebHistory } from 'vue-router'

const routes = [
	{
		name: 'home',
		path: '/',
		redirect: { name: 'releaseNotes' },
	},
	{
		path: '/manual/:pathMatch(.*)*', // For any old links that go to the User Manual, since renamed User Guide. This redirect could be removed in the future.
		redirect: (to) => {
			const childPath = to.params.pathMatch

			return childPath ? `/guide/${childPath}` : '/guide'
		},
	},
	{
		name: 'createDeck',
		path: '/create-deck',
		component: () =>
			import(/* webpackChunkName: "create-deck" */ '@/views/CreateDeck.vue'),
	},
	{
		/* When a `deck` URL lacks a specified deck path, redirect to the home page. */
		path: '/deck',
		redirect: { name: 'home' },
	},
	{
		path: '/deck/:deckPath',
		component: () =>
			import(/* webpackChunkName: "deck" */ '@/views/DeckWorkspace.vue'),
		children: [
			{
				name: 'listEditor',
				path: '',
				component: () =>
					import(
						/* webpackChunkName: "list-editor" */ '@/views/ListEditor.vue'
					),
			},
			{
				name: 'statsAnalyzer',
				path: 'stats-analyzer',
				component: () =>
					import(
						/* webpackChunkName: "stats-analyzer" */ '@/views/StatsAnalyzer.vue'
					),
			},
			{
				name: 'drawSim',
				path: 'draw-sim',
				component: () =>
					import(/* webpackChunkName: "draw-sim" */ '@/views/DrawSim.vue'),
			},
			{
				name: 'deckExporter',
				path: 'deck-exporter',
				component: () =>
					import(
						/* webpackChunkName: "deck-exporter" */ '@/views/DeckExporter.vue'
					),
			},
			{
				// Outdated links may still point to the old name of the Deck Exporter mode page. This redirect could be removed in the future.
				path: 'list-takeout',
				redirect: { name: 'deckExporter' },
			},
			{
				// Outdated links may still point to the old name of the Deck Exporter mode page. This redirect could be removed in the future.
				path: 'text-renderer',
				redirect: { name: 'deckExporter' },
			},
			{
				name: 'listEntry',
				path: 'list-entry',
				component: () =>
					import(/* webpackChunkName: "list-entry" */ '@/views/ListEntry.vue'),
			},
		],
	},
	{
		name: 'archiveDecks',
		path: '/archive-decks',
		meta: { forceDeckActionScroll: true },
		component: () =>
			import(
				/* webpackChunkName: "archive-decks" */ '@/views/ArchiveDecks.vue'
			),
	},
	{
		name: 'deleteDecks',
		path: '/delete-decks',
		component: () =>
			import(/* webpackChunkName: "delete-decks" */ '@/views/DeleteDecks.vue'),
	},
	{
		name: 'releaseNotes',
		path: '/release-notes',
		component: () =>
			import(
				/* webpackChunkName: "release-notes" */ '@/views/ReleaseNotes.vue'
			),
	},
	{
		/* This route should always be the last item in the `routes` array. */
		name: 'notFound',
		path: '/:pathMatch(.*)*',
		component: () =>
			import(/* webpackChunkName: "not-found" */ '@/views/NotFound.vue'),
	},
]

const router = createRouter({
	history: createWebHistory(),
	routes,
	scrollBehavior(to, from, savedPosition) {
		if (to.meta.forceDeckActionScroll) {
			return {
				el: '.deck-action-page',
				behavior: 'smooth',
				top: 27,
			}
		}

		if (savedPosition) {
			return savedPosition
		}

		if (to.hash) {
			return {
				el: to.hash,
			}
		}

		// When switching between workspace modes for the same deck, do not change scroll position from router.js.
		if (to.params.deckPath && from.params.deckPath === to.params.deckPath) {
			return false
		}

		return { top: 0, left: 0 }
	},
})

const legacyRedirectPaths = [
	'/contact',
	'/donate',
	'/guide',
	'/privacy',
	'/terms',
]

function getLegacyDomainRedirectURL(to) {
	if (window.location.hostname.toLowerCase() !== 'magic.iristormdesign.com') {
		return null
	}

	const matchingPath = legacyRedirectPaths.find(
		(path) => to.path === path || to.path.startsWith(`${path}/`),
	)

	if (!matchingPath) {
		return null
	}

	return `https://mtgcarpenter.com${to.fullPath}?src=mdb-redirect`
}

router.beforeEach((to) => {
	const redirectURL = getLegacyDomainRedirectURL(to)

	if (!redirectURL) {
		return true
	}

	window.location.replace(redirectURL)

	return false
})

export default router
